import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

/**
 * Administrace turnajů (spec 17.10) — seznam turnajů sezóny a zakládání nového. Detail turnaje
 * (nastavení, popis, obsazovačka, účastníci, výsledky) žije v [TournamentAdminScreen].
 *
 * Nahrazuje jednoduchou mřížku z v1.41: ta zůstává dostupná jako typ `simple`, pod kterým jsou
 * i všechny turnaje založené před touhle verzí.
 */
@Composable
fun TournamentsScreen(token: String?, role: String?, seasonId: String, seasonName: String) {
    var tournaments by remember(seasonId) { mutableStateOf<List<TournamentDto>>(emptyList()) }
    var loading by remember(seasonId) { mutableStateOf(true) }
    var error by remember(seasonId) { mutableStateOf<String?>(null) }
    var refreshKey by remember(seasonId) { mutableStateOf(0) }
    var creating by remember(seasonId) { mutableStateOf(false) }
    var openId by remember(seasonId) { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(seasonId, refreshKey) {
        loading = true
        try {
            tournaments = Api.fetchTournaments(token, seasonId)
            error = null
        } catch (e: Throwable) {
            error = "Načtení turnajů selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    val open = tournaments.firstOrNull { it.id == openId }
    if (open != null) {
        TournamentAdminScreen(
            token = token,
            role = role,
            seasonId = seasonId,
            tournament = open,
            onChanged = { refreshKey++ },
            onBack = { openId = null; refreshKey++ },
        )
        return
    }

    if (creating) {
        NewTournamentForm(
            token = token,
            seasonId = seasonId,
            existingSlugs = tournaments.map { it.slug },
            onCancel = { creating = false },
            onCreated = { created ->
                creating = false
                tournaments = tournaments + created
                openId = created.id
                refreshKey++
            },
        )
        return
    }

    Div(attrs = { classes("card", "tournament-header") }) {
        H2 { Text("Turnaje — sezóna $seasonName") }
        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }

        if (role.canManage()) {
            Div(attrs = { classes("actions") }) {
                Button(attrs = { onClick { creating = true } }) { Text("Nový turnaj") }
            }
        }

        if (tournaments.isEmpty()) {
            if (!loading) Text("Zatím žádné turnaje.")
        } else {
            Table(attrs = { classes("public-table", "tournament-list-table") }) {
                Tr {
                    Th { Text("Název") }
                    Th { Text("Typ") }
                    Th { Text("Stav") }
                    Th { Text("Datum") }
                    Th { Text("Her") }
                    if (role.isSuperadmin()) Th { }
                }
                tournaments.forEach { t ->
                    Tr(attrs = { classes("clickable-row") }) {
                        Td(attrs = { onClick { openId = t.id } }) { Text(t.title) }
                        Td(attrs = { onClick { openId = t.id } }) { Text(tournamentTypeLabel(t.type)) }
                        Td(attrs = { onClick { openId = t.id } }) {
                            Span(attrs = { classes("tag") }) { Text(tournamentStatusLabel(t.status)) }
                        }
                        Td(attrs = { onClick { openId = t.id } }) { Text(formatTournamentDateRange(t.startsAt, t.endsAt)) }
                        Td(attrs = { onClick { openId = t.id } }) { Text(t.gamesPerPlayer.toString()) }
                        if (role.isSuperadmin()) {
                            Td {
                                // Smazání celého turnaje zůstává jen superadminovi (spec 13.5);
                                // účastníci i skóre zmizí kaskádově a trigger stihne zneplatnit
                                // player_stats_cache ještě před smazáním (spec 13.4).
                                Button(attrs = {
                                    onClick {
                                        if (window.confirm("Opravdu smazat turnaj „${t.title}“ včetně všech výsledků?")) {
                                            scope.launch {
                                                try {
                                                    Api.deleteTournament(token, t.id)
                                                    refreshKey++
                                                } catch (e: Throwable) {
                                                    error = "Smazání selhalo: ${e.apiMessage()}"
                                                }
                                            }
                                        }
                                    }
                                }) { Text("Smazat") }
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Založení turnaje: název, typ a datum. Typ se dál nemění — určuje, co se konfiguruje a co se
 * zobrazuje (spec 17.1), a změna typu by osiřela typově specifická data. Nový turnaj vzniká jako
 * koncept (veřejně neviditelný, spec 17.11) a rovnou se otevře k doplnění.
 */
@Composable
private fun NewTournamentForm(
    token: String?,
    seasonId: String,
    existingSlugs: List<String>,
    onCancel: () -> Unit,
    onCreated: (TournamentDto) -> Unit,
) {
    var title by remember { mutableStateOf("") }
    var type by remember { mutableStateOf(TOURNAMENT_BRACKET) }
    var startsAt by remember { mutableStateOf("") }
    var endsAt by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    val slug = remember(title, existingSlugs) { uniqueTournamentSlug(title, existingSlugs) }

    Div(attrs = { classes("card", "tournament-form") }) {
        H3 { Text("Nový turnaj") }
        error?.let { Div(attrs = { classes("editor-error") }) { Text("Chyba: $it") } }

        Div {
            Label { Text("Název: ") }
            Input(type = InputType.Text) {
                classes("wide-input")
                value(title)
                onInput { title = it.value }
            }
        }
        Div {
            Label { Text("Typ: ") }
            Select(attrs = { onChange { type = it.value ?: TOURNAMENT_BRACKET } }) {
                TOURNAMENT_TYPES.forEach { (value, label) ->
                    Option(value, attrs = { if (value == type) selected() }) { Text(label) }
                }
            }
        }
        Div {
            Label { Text("Od: ") }
            Input(type = InputType.Date) {
                value(startsAt)
                onInput {
                    val wasSameAsStart = endsAt.isBlank() || endsAt == startsAt
                    startsAt = it.value
                    if (wasSameAsStart) endsAt = it.value
                }
            }
            Label { Text(" Do: ") }
            Input(type = InputType.Date) { value(endsAt); onInput { endsAt = it.value } }
        }
        if (title.isNotBlank()) {
            P(attrs = { classes("muted") }) { Text("Veřejná adresa: /$slug") }
        }

        Div(attrs = { classes("save-action") }) {
            Button(attrs = {
                if (saving || title.isBlank() || (endsAt.isNotBlank() && startsAt.isBlank()) ||
                    (startsAt.isNotBlank() && endsAt.isNotBlank() && endsAt < startsAt)) disabled()
                onClick {
                    saving = true
                    error = null
                    scope.launch {
                        try {
                            val created = Api.createTournament(
                                token,
                                TournamentInput(
                                    seasonId = seasonId,
                                    title = title.trim(),
                                    slug = slug,
                                    type = type,
                                    status = "draft",
                                    startsAt = startsAt.ifBlank { null },
                                    endsAt = endsAt.takeIf { it.isNotBlank() },
                                    bodyHtml = null,
                                    config = tournamentConfigJson(type, defaultTournamentConfig(type)),
                                ),
                            )
                            onCreated(created)
                        } catch (e: Throwable) {
                            error = "Založení selhalo: ${e.apiMessage()}"
                            saving = false
                        }
                    }
                }
            }) { Text(if (saving) "Zakládám…" else "Založit turnaj") }
            Button(attrs = { onClick { onCancel() } }) { Text("Zpět") }
        }
    }
}

/** Slug z názvu, s číselnou příponou při kolizi uvnitř sezóny (unikátnost hlídá i index v DB). */
fun uniqueTournamentSlug(title: String, existing: List<String>, ignore: String? = null): String {
    val base = slugify(title).ifBlank { "turnaj" }
    val taken = existing.filter { it != ignore }.toSet()
    if (base !in taken) return base
    var i = 2
    while ("$base$i" in taken) i++
    return "$base$i"
}
